/**
 * Iter-228x-bis — Global expense search page.
 * Single unified interface with a scope selector covering:
 *   • Mes NDF        (mine)
 *   • NDF validées   (validated)  — CEO/ADMIN only
 *   • Cabinet        (cabinet)    — CEO/ADMIN only
 *   • Par utilisateur (user)      — CEO/ADMIN only, with user picker
 */
import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import CrossMonthSearchPanel from "../components/CrossMonthSearchPanel";

const ALL_CATEGORIES = [
  "Restauration", "Carburant", "Hôtel", "Transport", "Alimentation",
  "Électronique", "Loisirs", "Abonnements", "Loyer", "Licences logicielles",
  "Honoraires", "Fournitures", "Marketing", "Banque", "Comptabilité",
  "Juridique", "Salaires", "Charges sociales", "Télécom & Internet",
  "Formation", "Assurances", "Autre",
];

export default function ExpenseSearchPage() {
  const { user, hasPermission } = useAuth();
  const isPrivileged = user?.role === "CEO" || user?.role === "ADMIN";

  const SCOPES = useMemo(() => [
    { id: "mine", label: "Mes NDF", desc: "Mes notes de frais personnelles", available: true },
    { id: "validated", label: "NDF validées", desc: "Toutes les NDF approuvées ou réglées", available: isPrivileged },
    { id: "cabinet", label: "Dépenses Cabinet", desc: "Dépenses internes du cabinet", available: isPrivileged && hasPermission("manage_cabinet_expenses") },
    { id: "user", label: "Par utilisateur", desc: "Filtrer les NDF d'un collaborateur précis", available: isPrivileged },
    { id: "all", label: "Toutes (audit)", desc: "Cabinet + NDF (toutes statuts, tous utilisateurs) — audit transversal", available: isPrivileged && hasPermission("manage_cabinet_expenses") },
  ].filter((s) => s.available), [isPrivileged, hasPermission]);

  const [scope, setScope] = useState(SCOPES[0]?.id || "mine");
  const [users, setUsers] = useState([]);
  const [targetUserId, setTargetUserId] = useState("");

  // Load directory users for the "Par utilisateur" picker (CEO/ADMIN only).
  useEffect(() => {
    if (scope !== "user" || !isPrivileged) return;
    api.get("/admin/users")
      .then((r) => setUsers((r.data || []).filter((u) => u.status !== "DISABLED")))
      .catch((e) => toast.error(e?.response?.data?.detail || "Échec du chargement des utilisateurs"));
  }, [scope, isPrivileged]);

  const csvPrefix = scope === "user" && targetUserId
    ? `ndf_user_${targetUserId}`
    : `recherche_${scope}`;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto" data-testid="expense-search-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Search size={22} className="text-zinc-500" /> Recherche globale
        </h1>
        <p className="text-zinc-600 text-sm mt-1">
          Cherchez parmi toutes les dépenses (cabinet, NDF, NDF validées) — par catégorie,
          fournisseur, période. Export CSV inclus.
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-4">
        <div className="text-[11px] uppercase tracking-wider font-mono text-zinc-500 mb-2">Portée de la recherche</div>
        <div className="flex flex-wrap gap-2">
          {SCOPES.map((s) => (
            <button
              key={s.id} type="button"
              onClick={() => { setScope(s.id); if (s.id !== "user") setTargetUserId(""); }}
              data-testid={`scope-${s.id}`}
              className={`px-3 py-1.5 text-sm rounded border ${scope === s.id ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"}`}
              title={s.desc}
            >
              {s.label}
            </button>
          ))}
        </div>
        {scope === "user" && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Collaborateur :</label>
            <select
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              data-testid="user-picker"
              className="text-sm px-2 py-1.5 border border-zinc-300 rounded bg-white min-w-[260px]"
            >
              <option value="">— Choisir —</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {scope === "user" && !targetUserId ? (
        <div className="bg-zinc-50 border border-dashed border-zinc-300 rounded-md py-12 text-center text-sm text-zinc-500">
          Sélectionnez un collaborateur pour lancer la recherche.
        </div>
      ) : (
        <CrossMonthSearchPanel
          key={`${scope}-${targetUserId}`}
          kind={scope}
          target_user_id={scope === "user" ? targetUserId : null}
          testidPrefix={`global-search-${scope}`}
          csvFilenamePrefix={csvPrefix}
          categories={ALL_CATEGORIES}
          initialOpen={true}
        />
      )}
    </div>
  );
}
